import calculateAdvancedFieldScore from './calculateAdvancedFieldScore'
/**
 * State-of-the-art fuzzy search with intelligent multi-algorithm scoring
 */
const advancedFuzzySearch = (users, searchTerm) => {
  if (!searchTerm.trim()) {
    return users.map(user => ({ ...user, searchScore: 1, matchDetails: [] }))
  }

  const results = users.map(user => {
    // Calculate scores for each field with optimized weights
    const nameScore = calculateAdvancedFieldScore(searchTerm, user.name, 1.0)
    const emailScore = calculateAdvancedFieldScore(searchTerm, user.email.split('@')[0], 0.8)
    const roleScore = calculateAdvancedFieldScore(searchTerm, user.role, 0.6)
    const entitiesText = Array.isArray(user.entities) 
      ? user.entities.join(' ') 
      : (user.entities || '')
    const entityScore = calculateAdvancedFieldScore(searchTerm, entitiesText, 0.7)

    // Intelligent score combination
    const scores = [nameScore, emailScore, roleScore, entityScore]
    const validScores = scores.filter(s => s.score > 0)

    if (validScores.length === 0) {
      return { ...user, searchScore: 0, matchDetails: [], bestMatch: { score: 0, type: 'none' } }
    }

    // Use the best single match as primary score with bonus for multiple matches
    const bestScore = Math.max(...validScores.map(s => s.score))
    const multiMatchBonus = validScores.length > 1 ? 0.05 : 0
    const totalScore = Math.min(1.0, bestScore + multiMatchBonus)

    const matchDetails = [
      nameScore.score > 0 && { field: 'name', ...nameScore },
      emailScore.score > 0 && { field: 'email', ...emailScore },
      roleScore.score > 0 && { field: 'role', ...roleScore },
      entityScore.score > 0 && { field: 'entities', ...entityScore },
    ].filter(Boolean)

    const bestMatch = validScores.reduce((best, current) =>
      current.score > best.score ? current : best
    )

    return {
      ...user,
      searchScore: totalScore,
      matchDetails,
      bestMatch,
    }
  })

  // Intelligent sorting with type priority
  return results
    .filter(user => user.searchScore > 0.1)
    .sort((a, b) => {
      // Primary sort: score difference threshold
      const scoreDiff = b.searchScore - a.searchScore
      if (Math.abs(scoreDiff) > 0.05) {
        return scoreDiff
      }

      // Secondary sort: match type priority
      const typeWeights = {
        exact: 10,
        prefix: 9,
        acronym: 8,
        word_prefix: 7,
        substring: 6,
        word_substring: 5,
        jaro_winkler: 4,
        word_jaro: 3,
        ngram: 2,
        word_ngram: 1,
        none: 0,
      }

      const aWeight = typeWeights[a.bestMatch.type] || 0
      const bWeight = typeWeights[b.bestMatch.type] || 0

      if (bWeight !== aWeight) {
        return bWeight - aWeight
      }

      // Tertiary sort: alphabetical
      return a.name.localeCompare(b.name)
    })
}

export default advancedFuzzySearch
